import React, { createContext, Dispatch, FC, useContext, useEffect, useState } from "react";
import AboutPage from "./components/AboutPage";
import MapDownloadPage from "./components/map/MapDownloadPage";
import ObjectPreviewPage from "./components/object/ObjectPreviewPage";
import ObjectModifyPage from "./components/object/ObjectModifyPage";
import ObjectCreatePage from "./components/object/ObjectCreatePage";
import ObjectEditFormPage from "./components/object/ObjectEditFormPage";
import ObjectPage from "./components/object/ObjectPage";
import InventoryEditPage from "./components/inventory/InventoryEditPage";
import InventoryFormEditPage from "./components/inventory/InventoryFormEditPage";
import InventoryStyleEditPage from "./components/inventory/InventoryStyleEditPage";
import InventoryMapListPage from "./components/inventory/InventoryMapListPage";
import LogoutPage from "./components/LogoutPage";
import AttributeFilterPage from "./components/inventory/AttributeFilterPage";
import OfflineMapsPage from "./components/map/OfflineMapsPage";
import ProjectCreatePage from "./components/project/ProjectCreatePage";
import ProjectEditPage from "./components/project/ProjectEditPage";
import ProjectPage from "./components/project/ProjectPage";
import ProjectListPage from "./components/project/ProjectListPage";
import DatabasePage from "./components/database/DatabasePage";
import SyncPage from "./components/database/SyncPage";
import Sidebar from "./components/common/navigation/Sidebar";
import Topbar from "./components/common/navigation/Topbar";
import Bottombar from "./components/common/navigation/Bottombar";
import Map from "./components/map/Map";
import { Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import LoginModal from "./components/LoginModal";
import { useJWT, useRawJWT, useSetLastInventoryId } from "./state";
import DatabaseSelectPage from "./components/database/DatabaseSelectPage";
import Client from "swecomap/src/swecomap/Client";
import { useSetDatabaseJWT } from "./hooks/database/useDatabases";
import { useCurrentInventoryId } from "./hooks/url";
import InventoryCreatePage from "./components/inventory/InventoryCreatePage";
import { applyUpdate, useUpdateReady } from "./serviceWorkerControl";
import { StatusBar } from "./components/common/navigation/StatusBar";
import { useNetworkState } from "./hooks/useNetworkState";
import { useContinuousSync } from "./hooks/database/useContinuousSync";
import { useDatabaseStatus } from "./hooks/database/useDatabaseStatus";
import Bugsnag from "@bugsnag/js";

const MapClientContext = createContext<{ get: Client | undefined; set: Dispatch<Client | undefined> } | undefined>(undefined);
export const useMap = () => useContext(MapClientContext)?.get?.map;
export const useMapClient = () => useContext(MapClientContext)?.get;
export const useSetMapClient = () => useContext(MapClientContext)?.set;

const InventoryContainer: FC = () => {
  const mapClient = useMapClient();

  const { inventoryId } = useCurrentInventoryId();
  const setLastInventoryId = useSetLastInventoryId();
  useEffect(() => {
    setLastInventoryId(inventoryId || null);
  }, [setLastInventoryId, inventoryId]);

  const { state } = useNetworkState();
  const { sync } = useDatabaseStatus();
  useContinuousSync(inventoryId!, state === "online");
  useEffect(() => {
    // sync when changing to online, or changing to the inventory
    if (state === "online") {
      sync([inventoryId!]).then(() => {});
    }
    // sync when changing away from the inventory
    return () => {
      if (state === "online" || state === "online-slow") {
        sync([inventoryId!]).then(() => {});
      }
    };
  }, [state, sync, inventoryId]);

  return (
    <>
      <StatusBar />

      <main>
        {/* Most parts of the inventory require the map to be available */}
        {mapClient ? <Outlet /> : null}

        <Sidebar />
        <Topbar />
        <Bottombar />
        <Map />
      </main>
    </>
  );
};

function App() {
  const [mapClient, setMapClient] = useState<Client>();

  const location = useLocation();
  const navigate = useNavigate();

  const jwt = useRawJWT();
  const parsedJwt = useJWT();

  useEffect(() => {
    Bugsnag.setUser(undefined, parsedJwt?.userId);

    if (jwt && location.pathname.startsWith("/jwt/")) {
      navigate("/", { replace: true });
    }
  }, [jwt, location, navigate, parsedJwt?.userId]);

  const setDatabaseJWT = useSetDatabaseJWT();
  setDatabaseJWT(jwt);

  const updateReady = useUpdateReady();

  return (
    <>
      {updateReady ? (
        <div className="update-notification">
          <div>
            <span>A new version of Piano is available</span>
            <button onClick={() => applyUpdate()} className="button button-fill">
              Refresh
            </button>
          </div>
        </div>
      ) : null}
      {!jwt ? (
        <LoginModal />
      ) : (
        <MapClientContext.Provider value={{ get: mapClient, set: setMapClient }}>
          <Routes>
            <Route path="/" element={<DatabaseSelectPage />} />

            <Route path="/about" element={<AboutPage />} />
            <Route path="/logout" element={<LogoutPage />} />

            <Route path="/d/:databaseId">
              <Route index element={<DatabasePage />} />
              <Route path="sync" element={<SyncPage />} />
              <Route path="p">
                <Route index element={<ProjectListPage />} />
                <Route path="new" element={<ProjectCreatePage />} />
                <Route path=":projectId">
                  <Route index element={<ProjectPage />} />
                  <Route path="edit" element={<ProjectEditPage />} />
                  <Route path="i/new" element={<InventoryCreatePage />} />
                </Route>
              </Route>

              <Route path="i/:inventoryId" element={<InventoryContainer />}>
                <Route path="edit" element={<InventoryEditPage />} />
                <Route path="filter" element={<AttributeFilterPage />} />
                <Route path="list" element={<InventoryMapListPage />} />
                <Route path="o/new" element={<ObjectCreatePage />} />
                <Route path="o/:objectId">
                  <Route index element={<ObjectPage />} />
                  <Route path="preview" element={<ObjectPreviewPage />} />
                  <Route path="modify" element={<ObjectModifyPage />} />
                  <Route path="f/new/:templateId" element={<ObjectEditFormPage />} />
                  <Route path="f/:formId" element={<ObjectEditFormPage />} />
                </Route>
                <Route path="form">
                  <Route path="new/*" element={<InventoryFormEditPage />} />
                  <Route path=":formId/*" element={<InventoryFormEditPage />} />
                </Route>
                <Route path="style">
                  <Route path="new" element={<InventoryStyleEditPage />} />
                  <Route path=":style" element={<InventoryStyleEditPage />} />
                </Route>
                {/* Offline maps aren't actually inventory, or even project or database, specific, but we want to render a map while using them */}
                <Route path="offline-maps">
                  <Route index element={<OfflineMapsPage />} />
                  <Route path="new" element={<MapDownloadPage />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </MapClientContext.Provider>
      )}
    </>
  );
}

export default App;
